// Browser alerts for glucose threshold crossings.
// Prefs are stored locally per browser/device (not per user in DB) — deliberate,
// since notifications are inherently a per-device consent surface.

const PREFS_KEY = "gc.alertPrefs.v1";
const STATE_KEY = "gc.alertState.v1"; // last-fired timestamps keyed by user_id + zone

const DEFAULTS = {
  enabled: false,
  sound: true,
  cooldownMin: 15,
  onUrgentLow: true,
  onUrgentHigh: true,
  onLow: false,   // between urgent-low and target-low
  onHigh: false,  // between target-high and urgent-high
  maxAgeMin: 15,  // don't alert on readings older than this
};

export function getAlertPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULTS };
  }
}

export function setAlertPrefs(patch) {
  const merged = { ...getAlertPrefs(), ...patch };
  localStorage.setItem(PREFS_KEY, JSON.stringify(merged));
  return merged;
}

export function notificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function requestNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  try {
    const p = await Notification.requestPermission();
    return p;
  } catch {
    return "denied";
  }
}

// Zone classification with 5 buckets so we can alert per zone.
export function classifyZone(sgv, alertLow, alertHigh, targetLow, targetHigh) {
  if (sgv == null) return "none";
  if (sgv < alertLow) return "urgent-low";
  if (sgv > alertHigh) return "urgent-high";
  if (sgv < targetLow) return "low";
  if (sgv > targetHigh) return "high";
  return "in-range";
}

const ZONE_META = {
  "urgent-low":  { title: "Urgent Low glucose",  urgent: true,  emoji: "⬇" },
  "urgent-high": { title: "Urgent High glucose", urgent: true,  emoji: "⬆" },
  "low":         { title: "Low glucose",         urgent: false, emoji: "↓" },
  "high":        { title: "High glucose",        urgent: false, emoji: "↑" },
};

function loadState() {
  try { return JSON.parse(localStorage.getItem(STATE_KEY)) || {}; }
  catch { return {}; }
}
function saveState(s) { localStorage.setItem(STATE_KEY, JSON.stringify(s)); }

let _audioCtx = null;
function playBeep() {
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    // Two-tone urgent beep
    const now = ctx.currentTime;
    const beep = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, now + start);
      g.gain.exponentialRampToValueAtTime(0.25, now + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      o.connect(g).connect(ctx.destination);
      o.start(now + start);
      o.stop(now + start + dur + 0.02);
    };
    beep(880, 0, 0.18);
    beep(1320, 0.22, 0.22);
  } catch { /* ignore */ }
}

function fireNativeNotification(title, body, tag) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;
  try {
    // eslint-disable-next-line no-new
    new Notification(title, { body, tag, renotify: true, silent: false });
    return true;
  } catch {
    return false;
  }
}

/**
 * Evaluate the latest reading and fire alerts if warranted.
 * Returns `{ zone, fired: boolean, reason?: string }`.
 *
 * Safe to call every render; internal cooldowns prevent spam.
 */
export function evaluateReading({
  userId,
  sgv,
  ts,          // epoch ms of the reading
  units,       // 'mg/dL' | 'mmol/L' — for message formatting
  alertLow, alertHigh, targetLow, targetHigh,
  onFire,      // optional callback ({ zone, message })
}) {
  const prefs = getAlertPrefs();
  const zone = classifyZone(sgv, alertLow, alertHigh, targetLow, targetHigh);
  if (!prefs.enabled) return { zone, fired: false, reason: "disabled" };
  if (zone === "in-range" || zone === "none") return { zone, fired: false };
  const meta = ZONE_META[zone];
  if (!meta) return { zone, fired: false };
  // per-zone opt-in
  const flag = { "urgent-low": "onUrgentLow", "urgent-high": "onUrgentHigh", "low": "onLow", "high": "onHigh" }[zone];
  if (!prefs[flag]) return { zone, fired: false, reason: "zone-off" };
  // freshness — don't fire on stale readings
  if (ts && Date.now() - ts > prefs.maxAgeMin * 60 * 1000) {
    return { zone, fired: false, reason: "stale" };
  }
  // cooldown per (user, zone)
  const state = loadState();
  const key = `${userId || "self"}:${zone}`;
  const last = state[key] || 0;
  const cooldownMs = prefs.cooldownMin * 60 * 1000;
  if (Date.now() - last < cooldownMs) return { zone, fired: false, reason: "cooldown" };

  // Format the message
  const display = units === "mmol/L"
    ? `${(sgv * 0.0555).toFixed(1)} mmol/L`
    : `${Math.round(sgv)} mg/dL`;
  const title = `${meta.emoji} ${meta.title}`;
  const body = `Reading: ${display} · ${new Date(ts || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;

  const fired = fireNativeNotification(title, body, `gc-${zone}`);
  if (prefs.sound && (meta.urgent || zone === "low" || zone === "high")) playBeep();

  state[key] = Date.now();
  saveState(state);
  onFire?.({ zone, title, body });
  return { zone, fired, title, body };
}

// Fire a synthetic "test" alert without affecting cooldowns.
export function fireTestAlert(units) {
  const prefs = getAlertPrefs();
  const display = units === "mmol/L" ? "3.7 mmol/L" : "66 mg/dL";
  const fired = fireNativeNotification("⬇ Test alert — Urgent Low", `Reading: ${display} · GlucoCloud alerts are working.`, "gc-test");
  if (prefs.sound) playBeep();
  return fired;
}
